/**
 * PupilFrameworkCard — per-pupil quick card on /instructor.
 *
 * Loads /api/learner-framework/pupil/{id}, shows:
 *   • Pupil name + Responsibility editor (snap to 10) — inline +/- buttons
 *   • Test Readiness gauge
 *   • Stage Mastery + Lessons + Mindset chips
 *   • Top foundation gap (if any) with severity badge
 *   • Recommended focus skill from foundation gaps
 *
 * Premium feel: dark accent header, glass-morphic body, no clutter.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp, Activity, Brain, AlertTriangle, ChevronRight,
  Plus, Minus, Loader2, Target, Smile,
} from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

function gaugeColor(pct) {
  if (pct >= 80) return "bg-emerald-500";
  if (pct >= 60) return "bg-indigo-500";
  if (pct >= 40) return "bg-amber-500";
  return "bg-rose-500";
}

const POSITIVE = new Set(["calm", "focused", "ready", "confident", "motivated"]);
const NEGATIVE = new Set(["anxious", "overthinking", "tired", "rushed", "low_confidence"]);


export default function PupilFrameworkCard({ pupilId, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const { data: d } = await axios.get(`${API}/api/learner-framework/pupil/${pupilId}`, { withCredentials: true });
      setData(d);
    } catch (e) {
      // Hide silently if pupil isn't a work_students row yet.
      console.warn("pupil framework load failed:", e?.response?.status);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pupilId]);

  async function setLevel(delta) {
    if (!data) return;
    const next = Math.max(0, Math.min(100, (data.responsibility_level || 0) + delta));
    if (next === data.responsibility_level) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/learner-framework/pupil/${pupilId}/responsibility`,
        { level: next },
        { withCredentials: true }
      );
      await load();
      onChange?.();
    } catch (e) {
      toast.error("Couldn't update", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  async function recompute() {
    setSaving(true);
    try {
      await axios.post(`${API}/api/learner-framework/pupil/${pupilId}/recompute`, {}, { withCredentials: true });
      await load();
      toast.success("Refreshed from latest lessons");
    } catch (e) {
      toast.error("Refresh failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="rounded-2xl ring-1 ring-slate-200 bg-white p-4 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading pupil framework…
      </div>
    );
  }
  if (!data) return null;

  const tr = data.test_readiness_pct ?? 0;
  const sm = data.stage_mastery_pct  ?? 0;
  const rl = data.responsibility_level ?? 0;
  const gap = data.foundation_gaps?.[0];
  const mood = data.mindset_today?.mindset_states?.[0]?.replace("_", " ");
  const moodTone =
    POSITIVE.has((mood || "").toLowerCase().replace(" ", "_")) ? "text-emerald-600" :
    NEGATIVE.has((mood || "").toLowerCase().replace(" ", "_")) ? "text-rose-600" :
    "text-slate-600";

  return (
    <div
      data-testid={`pupil-framework-card-${pupilId}`}
      className="rounded-2xl ring-1 ring-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition"
    >
      {/* Dark accent header — pupil name + responsibility editor */}
      <div className="bg-gradient-to-r from-slate-900 to-indigo-900 px-4 py-3 text-white flex items-center justify-between gap-3">
        <Link
          to={`/pupil/${pupilId}`}
          data-testid={`pupil-name-link-${pupilId}`}
          className="font-bold text-sm truncate hover:underline inline-flex items-center gap-2"
        >
          {/* Territory Phase 1 (May 25, 2026) — postcode capture pip.
              Green = captured + geocoded. Grey = no postcode yet.
              No noisy banners, no nags. Purely informational. */}
          <span
            data-testid={`postcode-pip-${pupilId}`}
            data-status={data.home_postcode_status || "missing"}
            title={
              data.home_postcode_status === "geocoded"
                ? "Postcode captured"
                : "Postcode not yet captured"
            }
            aria-label={
              data.home_postcode_status === "geocoded"
                ? "Postcode captured"
                : "Postcode not yet captured"
            }
            className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
              data.home_postcode_status === "geocoded" ? "bg-emerald-400" : "bg-slate-500"
            }`}
          />
          <span className="truncate">{data.name || "Pupil"}</span>
        </Link>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold">Resp.</span>
          <button
            data-testid={`resp-down-${pupilId}`}
            onClick={() => setLevel(-10)}
            disabled={saving || rl <= 0}
            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 inline-flex items-center justify-center"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span data-testid={`resp-value-${pupilId}`} className="font-extrabold text-sm w-10 text-center tabular-nums">{rl}%</span>
          <button
            data-testid={`resp-up-${pupilId}`}
            onClick={() => setLevel(+10)}
            disabled={saving || rl >= 100}
            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 inline-flex items-center justify-center"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Test readiness gauge */}
      <div className="px-4 pt-3">
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Test readiness</span>
          <span data-testid={`tr-${pupilId}`} className="text-xl font-black text-slate-900 tabular-nums">{Math.round(tr)}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full ${gaugeColor(tr)} transition-all`} style={{ width: `${Math.max(2, Math.min(100, tr))}%` }} />
        </div>
      </div>

      {/* 3 chips */}
      <div className="px-4 pt-3 grid grid-cols-3 gap-1.5">
        <MicroChip icon={Activity} label="Skill" value={`${Math.round(sm)}%`} testid={`sm-${pupilId}`} />
        <MicroChip
          icon={Brain}
          label="Mindset"
          value={mood || "—"}
          testid={`mood-${pupilId}`}
          valueClass={`capitalize ${moodTone}`}
        />
        <MicroChip
          icon={TrendingUp}
          label="Last update"
          value={data.skill_progress?.[0]?.last_signal_at
            ? new Date(data.skill_progress[0].last_signal_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
            : "—"}
          testid={`updated-${pupilId}`}
        />
      </div>

      {/* Foundation gap or recommended focus */}
      <div className="px-4 py-3 border-t border-slate-100 mt-3 bg-slate-50/60">
        {gap ? (
          <div data-testid={`gap-${pupilId}`} className="flex items-start gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-none ${gap.severity === "high" ? "bg-rose-100 text-rose-600" : "bg-amber-100 text-amber-700"}`}>
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                Foundation gap{gap.severity === "high" ? " · high" : ""}
              </div>
              <div className="text-sm font-bold text-slate-900 truncate">{gap.title}</div>
              <div className="text-xs text-slate-600 line-clamp-2">{gap.calm_message || gap.reason}</div>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-none">
              <Smile className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">All foundations clear</div>
              <div className="text-sm text-slate-700">Look at extending the strongest skill next.</div>
            </div>
          </div>
        )}
      </div>

      {/* Phase 3 — current stage + active homework */}
      {(data.current_stage?.label || data.homework_active?.length > 0) && (
        <div data-testid={`stage-row-${pupilId}`} className="px-4 py-2 border-t border-slate-100 flex items-center justify-between gap-2 text-xs">
          <div className="min-w-0 flex-1">
            <span className="text-slate-500 font-semibold">Stage:</span>{" "}
            <span className="font-bold text-slate-900 truncate">
              {data.current_stage?.label || "Stage 0"}
            </span>
          </div>
          {data.homework_active?.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 px-2 py-0.5 font-bold">
              {data.homework_active.length} HW active
            </span>
          )}
        </div>
      )}

      {/* Recommended focus footer */}
      <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs text-slate-700">
          <Target className="w-3.5 h-3.5 text-indigo-600" />
          <span className="font-semibold">Lesson focus:</span>
          <span data-testid={`focus-${pupilId}`} className="font-bold text-slate-900">
            {gap?.title || data.skill_progress?.find((s) => s.status === "in_progress")?.title || "Free practice"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={`recompute-${pupilId}`}
            onClick={recompute}
            disabled={saving}
            className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
            title="Refresh from latest lessons"
          >
            Refresh
          </button>
          <Link
            to={`/pupil/${pupilId}`}
            data-testid={`open-${pupilId}`}
            className="inline-flex items-center text-xs font-bold text-slate-700 hover:text-slate-900"
          >
            Open <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}


function MicroChip({ icon: Icon, label, value, testid, valueClass }) {
  return (
    <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 px-2 py-1.5">
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-slate-500 font-bold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div data-testid={testid} className={`mt-0.5 text-xs font-extrabold text-slate-900 truncate ${valueClass || ""}`}>
        {value}
      </div>
    </div>
  );
}
